import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus } from '../../hooks/useDialogFocus';

const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);

/** A member's controls for the league's share link: turn it on, copy, reset, turn it off. */
export function ShareLeagueDialog({ api, open, onClose }) {
  const dialogRef = useDialogFocus({ open, onClose });
  const inputRef = useRef(null);
  const [state, setState] = useState({ status: 'loading', share: null, error: null });
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'reset' | 'off' | null
  const [copied, setCopied] = useState(null); // 'copied' | 'select' | null
  // The data-focus key of the control that takes focus after the next render,
  // since every state change here unmounts the control that had it.
  const focusNext = useRef(null);

  useEffect(() => {
    const key = focusNext.current;
    const target = key && dialogRef.current?.querySelector(`[data-focus="${key}"]`);
    if (!target) return;
    focusNext.current = null;
    target.focus();
  });

  const load = useCallback(() => {
    setState({ status: 'loading', share: null, error: null });
    api.getShare()
      .then(share => setState({ status: 'ready', share, error: null }))
      .catch(() => setState({ status: 'error', share: null, error: "Couldn't load the share settings." }));
  }, [api]);

  useEffect(() => {
    if (!open) return;
    focusNext.current = null;
    setConfirming(null);
    setCopied(null);
    load();
  }, [open, load]);

  const settle = (share, error) => {
    focusNext.current = share.enabled ? 'copy' : 'on';
    setState({ status: 'ready', share, error });
    setConfirming(null);
    setCopied(null);
  };

  // A failed action rereads the share, so a link reset or turned off
  // elsewhere shows as it is now, under the failure message.
  const act = async (fn, failure) => {
    setBusy(true);
    try {
      settle(await fn(), null);
    } catch {
      const share = await api.getShare().catch(() => state.share);
      settle(share, failure);
    } finally {
      setBusy(false);
    }
  };

  const confirm = (which) => {
    focusNext.current = 'keep';
    setConfirming(which);
  };

  const cancelConfirm = () => {
    focusNext.current = confirming;
    setConfirming(null);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.share.url);
      setCopied('copied');
    } catch {
      inputRef.current?.focus();
      inputRef.current?.select();
      setCopied('select');
    }
  };

  if (!open) return null;
  const share = state.share;
  const since = share?.enabled ? `On since ${day(share.created_at)}${share.reset_at ? ` · reset ${day(share.reset_at)}` : ''}` : null;
  return createPortal(
    <div className="ledger-dialog-scrim" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <section ref={dialogRef} tabIndex={-1} className="ledger-panel ledger-scope ledger-dialog" role="dialog" aria-modal="true" aria-labelledby="share-league-title">
        <div className="ledger-panel-head">
          <h2 id="share-league-title" className="ledger-panel-title">Share this league</h2>
          <span className="ledger-state">{share?.enabled ? 'Sharing is on' : share ? 'Sharing is off' : ''}</span>
        </div>
        <div className="ledger-dialog-body">
          {state.status === 'loading' ? <p className="ledger-state">Loading…</p> : null}
          {state.status === 'error' ? (
            <>
              <p className="ledger-state is-bad is-wrap">{state.error}</p>
              <div className="ledger-dialog-acts"><button type="button" className="ledger-btn" onClick={load}>Retry</button></div>
            </>
          ) : null}
          {state.status === 'ready' && !share.enabled ? (
            <>
              <p className="ledger-dialog-copy">Anyone with the link can view this league&apos;s dashboard without signing in. Managers&apos; names are hidden; team names are shown.</p>
              {state.error ? <p className="ledger-state is-bad is-wrap">{state.error}</p> : null}
              <div className="ledger-dialog-acts">
                <button type="button" className="ledger-btn" disabled={busy} onClick={onClose}>Cancel</button>
                <button type="button" className="ledger-btn is-primary" data-focus="on" disabled={busy}
                  onClick={() => act(api.enableShare, "Couldn't turn on sharing. Try again.")}>Turn on sharing</button>
              </div>
            </>
          ) : null}
          {state.status === 'ready' && share.enabled ? (
            <>
              <p className="ledger-dialog-copy">Anyone with this link can view the dashboard. Managers&apos; names are hidden.</p>
              <div className="ledger-copy-row">
                <input ref={inputRef} className="ledger-input" readOnly value={share.url} aria-label="Share link" />
                <button type="button" className="ledger-btn is-primary" data-focus="copy" disabled={busy} onClick={copy}>Copy</button>
              </div>
              <span className={copied === 'copied' ? 'ledger-state is-good' : 'ledger-state'} aria-live="polite">
                {copied === 'copied' ? 'Copied' : copied === 'select' ? 'Link selected. Copy it from the field.' : since}
              </span>
              {state.error ? <p className="ledger-state is-bad is-wrap">{state.error}</p> : null}
              {confirming === 'reset' ? (
                <div className="ledger-confirm" role="alertdialog" aria-labelledby="share-league-confirm">
                  <p id="share-league-confirm">Reset the link? The current link stops working for everyone.</p>
                  <div className="ledger-dialog-acts">
                    <button type="button" className="ledger-btn" data-focus="keep" disabled={busy} onClick={cancelConfirm}>Keep it</button>
                    <button type="button" className="ledger-btn is-danger" disabled={busy}
                      onClick={() => act(api.resetShare, "Couldn't reset the link. Try again.")}>Reset link</button>
                  </div>
                </div>
              ) : null}
              {confirming === 'off' ? (
                <div className="ledger-confirm" role="alertdialog" aria-labelledby="share-league-confirm">
                  <p id="share-league-confirm">Turn off sharing? The link stops working for everyone.</p>
                  <div className="ledger-dialog-acts">
                    <button type="button" className="ledger-btn" data-focus="keep" disabled={busy} onClick={cancelConfirm}>Keep it on</button>
                    <button type="button" className="ledger-btn is-danger" disabled={busy}
                      onClick={() => act(api.disableShare, "Couldn't turn off sharing. Try again.")}>Turn off sharing</button>
                  </div>
                </div>
              ) : null}
              {confirming ? null : (
                <div className="ledger-dialog-acts">
                  <button type="button" className="ledger-btn" data-focus="reset" onClick={() => confirm('reset')}>Reset link</button>
                  <button type="button" className="ledger-btn is-danger" data-focus="off" onClick={() => confirm('off')}>Turn off sharing</button>
                  <button type="button" className="ledger-btn" disabled={busy} onClick={onClose}>Done</button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </section>
    </div>,
    document.body,
  );
}

export default ShareLeagueDialog;
